/// <amd-module name="Wiki/PrintPage/WikiPagePrintService" />
import { format } from "VSS/Core/Util/String";
import { Services, VssService } from "VSS/Platform/Context";
import { IVssThemeService } from "VSS/Platform/Theming";
import { IHeaderMetaDataState } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataTypes";
import { IWikiPagePrintService } from "Wiki/PrintPage/IWikiPagePrintService";
import * as Resources from "Wiki/PrintPage/Resources";
import { isFirefox, isIE } from "Wiki/ViewCommon/Utils/WikiHelper";

export class WikiPagePrintService extends VssService implements IWikiPagePrintService {
    public populateFrameAndPrint(frameId: string, title: string | undefined, headerMetaDataState: IHeaderMetaDataState, printContent: string): void {
        // We do not support print in firefox because of the issue: http://kb.mozillazine.org/Problems_printing_web_pages
        if (isFirefox()) {
            return;
        }

        let pageUpdateInfo = "";
        if (headerMetaDataState.pusher) {
            const displayName = headerMetaDataState.pusher.displayName;
            pageUpdateInfo = displayName;
        }

        const dayFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat(undefined, { month: "short", year: "numeric", day: "numeric" });
        const timeFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "numeric", timeZoneName: "short" });
        const pageLastEditedDate = headerMetaDataState.pushDate
            ? dayFormat.format(headerMetaDataState.pushDate) + Resources.AtString + timeFormat.format(headerMetaDataState.pushDate)
            : "";

        // printFrame is of type Window (browser dependent), contentDocument and document both dont exist in it at same time
        const pagePrintFrame = (window.frames as any)[frameId];

        const wikiPrintThemeClass = "wiki-print-theme";
        const themeService = this.pageContext.getService<IVssThemeService>("IVssThemeService");
        themeService.createThemedSelector(`.${wikiPrintThemeClass}`, t => (t.isDark ? themeService.getTheme("ms.vss-web.vsts-theme") : t));

        // We need links to get all styles for markdown
        let allStyles = "";
        const allLinks = document.getElementsByTagName("link");
        const allInlineStyles = document.getElementsByTagName("style");
        for (let i = 0; i < allLinks.length; i++) {
            allStyles += allLinks[i].outerHTML;
        }
        for (let i = 0; i < allInlineStyles.length; i++) {
            allStyles += allInlineStyles[i].outerHTML;
        }

        const metadata =
            pageUpdateInfo !== ""
                ? `<div class="wiki-header-metadata-container">
                <span class="wiki-metadata">
                    ${format(Resources.HeaderMetaDataPrintString, pageUpdateInfo, pageLastEditedDate)}
                </span>
            </div>`
                : "";

        const printBody = `<div class="wiki-page-container">
            <div class="wiki-header">
                <div class="wiki-title-container">
                    <span class="page-title">
                        ${title}
                    </span>
                </div>
                ${metadata}
            </div>
            <hr>
            <div class="content-container">
                ${printContent}
            </div>
        </div>`;

        const frame = document.getElementById(frameId);

        const printFrame = (): void => {
            const pagePrintFrame = (window.frames as any)[frameId];
            const contentWindow = isIE() ? pagePrintFrame : pagePrintFrame.contentWindow;
            const result = contentWindow.document.execCommand("print", false, null);
            if (!result) {
                contentWindow.focus();
                contentWindow.print();
            }
            if (frame) {
                frame.removeEventListener("load", printFrame);
            }
        };

        if (frame) {
            frame.addEventListener("load", printFrame);
        }

        const contentDocument = pagePrintFrame.contentDocument || pagePrintFrame.document;
        contentDocument.write("<!DOCTYPE html>");
        contentDocument.write("<html>");
        contentDocument.write(`<head>${allStyles}</head>`);
        contentDocument.write(`<body class="${wikiPrintThemeClass}">${printBody}</body>`);
        contentDocument.write("</html>");
        contentDocument.close();
    }
}

Services.add("wiki-printpage-service", { serviceFactory: WikiPagePrintService });
